import { initializeApp, getApps, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getFunctions, type Functions } from 'firebase/functions';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

export interface SnippitsFirebase {
  app: FirebaseApp;
  appCheck: AppCheck | null;
  auth: Auth;
  db: Firestore;
  functions: Functions;
  storage: FirebaseStorage;
}

export interface InitOptions {
  appCheckSiteKey?: string;
  appCheckDebug?: boolean;
}

let cached: SnippitsFirebase | null = null;

export function initFirebase(
  config: FirebaseOptions,
  options: InitOptions = {},
): SnippitsFirebase {
  if (cached) return cached;
  const app = getApps()[0] ?? initializeApp(config);

  // App Check must be initialized before any other Firebase service call so
  // the SDK attaches tokens to every outgoing request.
  let appCheck: AppCheck | null = null;
  if (options.appCheckSiteKey) {
    if (options.appCheckDebug && typeof self !== 'undefined') {
      (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(options.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  cached = {
    app,
    appCheck,
    auth: getAuth(app),
    db: getFirestore(app),
    functions: getFunctions(app, 'us-central1'),
    storage: getStorage(app),
  };
  return cached;
}
